import React from 'react';
import {
	EstimatorModeTypes,
	EstimatePortActionTypes,
	EstimatePortActionTypeLabels,
	ShortEstimatePortActionTypes,
	TimeZoneFormat,
} from '@shared/utils/constants';
import { PortRotationEntryGeneratorTypes } from '@shared/utils/port-rotation-entry-generator-types';
import { formatNumber } from '@shared/utils/formatNumber';
import type { Port } from '@api/utils/ports';
import { type ConsumptionSetProps } from '@api/models/consumption-set';
import { CargoDetails } from '@api/features/cargos/getCargoDetails';
import { PerformanceEntryProps } from '@api/models/performance-entry';
import { SeaPassageSettings } from '@api/utils/sequelize/calculateVoyageItineraryDetails/helpers/itineraryConstants';
import { FuelTypeValue } from '@api/utils/getVesselFuelGrades';
import { EditableColumns } from '@client/components/EditableTableRedux/EditableCellTableRedux';
import styles from '../styles/shared.module.css';
import { LocalPortRotation } from '../context/hooks/usePortRotationHandlers';
import {
	formatDays,
	getArrivalColumn,
	getConfigColumn,
	getDeleteColumn,
	getDepartureColumn,
	getDistanceColumn,
	getNegativeRobEntryIds,
	getPortColumn,
	getRouteOptionColumns,
	getSeaDaysColumn,
	getSeaMarginColumn,
	getSpeedColumn,
	getTypeColumn,
	isBallastLeg,
	renderPortNameWithRobWarning,
} from './portRotationSharedColumns';

type Props = {
	handlePortRotationEntryDeletion: (entry: LocalPortRotation) => void;
	onPortRotationChange: (entries: LocalPortRotation[], immediate?: boolean) => void;
	onPortRotationRowChange: (
		id: number,
		attributes: any
	) => void;
	onConfigUpdate: (id: number, routeSettings: SeaPassageSettings) => void;
	speedPopoverOpen: number | null;
	setSpeedPopoverOpen: React.Dispatch<React.SetStateAction<number | null>>;
	timeZoneFormat: TimeZoneFormat;
	ports: Port[];
	consumptionSets: Array<ConsumptionSetProps & { PerformanceEntries: Array<PerformanceEntryProps> }>;
	screens: Partial<Record<string, boolean>>;
	mode: EstimatorModeTypes;
	cargos: CargoDetails[];
	defaultConsumptionSetId: number | undefined;
	disabled?: boolean;
	portRotation: LocalPortRotation[];
	handlePortRotationConfigSave: (id: number, routeSettings: SeaPassageSettings) => void;
	requiredArrivalPopoverOpen: number | null;
	setRequiredArrivalPopoverOpen: React.Dispatch<React.SetStateAction<number | null>>;
	refreshVessel: () => void;
	hasCargoRelet: boolean;
	vesselFuelGrades: Array<FuelTypeValue>;
};

export const getPortRotationConsumptionColumns = ({
	handlePortRotationEntryDeletion,
	onConfigUpdate,
	onPortRotationRowChange,
	speedPopoverOpen,
	setSpeedPopoverOpen,
	timeZoneFormat,
	ports,
	consumptionSets,
	mode,
	cargos,
	defaultConsumptionSetId,
	disabled,
	portRotation,
	onPortRotationChange,
	handlePortRotationConfigSave,
	requiredArrivalPopoverOpen,
	setRequiredArrivalPopoverOpen,
	refreshVessel,
	hasCargoRelet,
	vesselFuelGrades,
}: Props): EditableColumns<LocalPortRotation> => {
	const negativeRobEntryIds = getNegativeRobEntryIds(portRotation, vesselFuelGrades);

	const stemColumns: EditableColumns<LocalPortRotation> = [];

	vesselFuelGrades.forEach((fuelGrade) => {
		['qty', 'ppt'].forEach((v) => {
			// @ts-ignore
			stemColumns.push({
				title: fuelGrade + ` - ${v}`,
				editable: true,
				type: 'number',
				dataIndex: ['stemValues', fuelGrade, v],
				inputProps: {},
			});
		});
	});

	const arrivalRobColumns = vesselFuelGrades.map((fuelGrade) => {
		return {
			title: fuelGrade,
			editable: false,
			type: 'number',
			dataIndex: `${fuelGrade}-arrival`,
			className: styles.calculatedNumber,
			inputProps: {},
		};
	});

	const departureRobColumns = vesselFuelGrades.map((fuelGrade) => {
		return {
			title: fuelGrade,
			editable: false,
			type: 'number',
			dataIndex: `${fuelGrade}-departure`,
			className: styles.calculatedNumber,
			inputProps: {},
		};
	});

	return [
		getDeleteColumn({
			onDelete: handlePortRotationEntryDeletion,
			disabled,
		}),
		getConfigColumn({
			onConfigSave: handlePortRotationConfigSave,
			disabled,
		}),
		getTypeColumn({
			disabled,
			actionTypes: EstimatePortActionTypes,
			actionTypeLabels: EstimatePortActionTypeLabels,
			shortActionTypeLabels: ShortEstimatePortActionTypes,
		}),
		getPortColumn({
			ports,
			disabled,
			isGeneratedPortEditable: isBallastLeg,
			renderValue: (p: any) => renderPortNameWithRobWarning(
				p.inputProps?.options?.find((o: any) => o.value === p.value)?.label ?? null,
				negativeRobEntryIds.has(p.row?.id),
			),
		}),
		getArrivalColumn({
			timeZoneFormat,
			cargos,
			portRotation,
			disabled,
			consumptionSets,
			requiredArrivalPopoverOpen,
			setRequiredArrivalPopoverOpen,
			onPortRotationRowChange,
			refreshVessel,
		}),
		getDepartureColumn({
			timeZoneFormat,
			disabled,
			localTimeWidth: 330,
		}),
		{
			title: 'ROB on arrival',
			// @ts-ignore 
			children: arrivalRobColumns.map((a) => ({
				...a,
				className: (row) => row.generatedBy === PortRotationEntryGeneratorTypes.BALLAST_LEG ?
					styles.disabled :
					styles.calculatedNumber,
			})),
			editable: false,
		},
		{
			title: 'Stems',
			// @ts-ignore 
			children: stemColumns.map((r) => ({
				...r,
				className: (row) => row.generatedBy === PortRotationEntryGeneratorTypes.BALLAST_LEG ?
					styles.disabled :
					undefined,
				width: 75,
			})),
			editable: true,
		},
		{
			title: 'ROB on departure',
			// @ts-ignore 
			children: departureRobColumns,
			editable: false,
		},
		getDistanceColumn({
			renderDistance: (value) => formatNumber(value),
		}),
		getSeaMarginColumn({ disabled }),
		getSpeedColumn({
			portRotation,
			consumptionSets,
			defaultConsumptionSetId,
			disabled,
			hasCargoRelet,
			speedPopoverOpen,
			setSpeedPopoverOpen,
			onPortRotationChange,
			onPortRotationRowChange,
		}),
		getSeaDaysColumn({
			title: 'Sea days',
			renderDays: formatDays,
		}),
		...getRouteOptionColumns({
			mode,
			disabled,
			onConfigUpdate,
		}),
	];
};
